
import { v4 as uuidv4 } from 'uuid';
import { Company } from '@/lib/types';
import { useCompanyStore } from '@/lib/store';

/**
 * Parse a CSV or text list of companies into an array of company objects
 * Format expected: Company Name, Location (optional), Staff Size (optional)
 */
export const parseCompanyList = (
  companyList: string, 
  delimiter: string = ","
): Partial<Company>[] => {
  const lines = companyList.trim().split("\n");
  
  return lines.map(line => {
    const [name, location, staffSize] = line.split(delimiter).map(item => item.trim());
    
    return {
      name,
      locations: location ? [location] : ["Australia"],
      staffSize: staffSize || "",
      createdAt: Date.now(),
      bio: `${name} is a company based in Australia.`
    };
  });
};

/**
 * Import a list of companies into the store
 * @param companyList CSV string or text list of companies
 * @param delimiter Character separating values in each line
 * @returns Number of companies imported
 */
export const importCompanies = (
  companyList: string,
  delimiter: string = ","
): number => {
  const companies = parseCompanyList(companyList, delimiter);
  const addCompany = useCompanyStore.getState().addCompany;
  
  // Add each company to the store
  companies.forEach(company => {
    addCompany(company);
  });
  
  return companies.length;
};

/**
 * Check if a company name already exists in the store
 */
export const companyExists = (companyName: string): boolean => {
  const companies = useCompanyStore.getState().companies;
  return companies.some(company => 
    company.name.toLowerCase() === companyName.toLowerCase()
  );
};

/**
 * Import a list of companies, skipping any that already exist
 * @returns Object containing counts of added and skipped companies
 */
export const importCompaniesSkipExisting = (
  companyList: string,
  delimiter: string = ","
): { added: number; skipped: number } => {
  const companiesToAdd = parseCompanyList(companyList, delimiter);
  const addCompany = useCompanyStore.getState().addCompany;
  
  let added = 0;
  let skipped = 0;
  
  companiesToAdd.forEach(company => {
    if (company.name && !companyExists(company.name)) {
      addCompany(company);
      added++;
    } else {
      skipped++;
    }
  });
  
  return { added, skipped };
};
